import type { CSSProperties, ReactNode } from "react"

/**
 * Figma 524:1897 (Landing template, 12.10), rebuilt from its own settings
 * rather than exported, so it stays sharp at any card size. The 1440×810
 * frame is one radial gradient (Figma transform → centre at the foot, radii
 * 77.8% of the width and 101.6% of the height) plus two blurred glows along
 * the bottom edge. Figma's layer-blur radius 232.7 is a Gaussian sigma of
 * about half that. The image fill under the gradient is fully covered in
 * Figma, so it is left out.
 */
export type FigmaBackground = {
  /** Radial stops: centre, the 85% stop and the rim. */
  c0: string
  c1: string
  c1At: number
  c2: string
  /** Gradient centre as a share of the frame height (1 = the foot). */
  cy: number
  /** Radii as shares of the frame: Figma's transform gives 77.8% / 101.6%. */
  rx: number
  ry: number
  /** The wide soft glow and the thin bright band along the foot. */
  glow: string
  glowOpacity: number
  band: string
  bandOpacity: number
  /** Extra shade at the foot where the card stacks (below lg). */
  foot: number
  /** Film grain over the whole card, after Figma's Noise effect (Mono). */
  noise: boolean
  noiseSize: number
  noiseDensity: number
  noiseColor: string
  noiseOpacity: number
}

/**
 * Eugene's tuning in live controls (12.10): a deeper, bluer ground. Figma's
 * own values were #B2CEE1 / #3B5570 at 85% / #2B3E4F, centre at the foot,
 * glow #83A7C9 at 20%, band #BCDBF1, white grain size 0.2 at 17%.
 */
export const FIGMA_BACKGROUND_DEFAULTS: FigmaBackground = {
  c0: "#2A629D",
  c1: "#234261",
  c1At: 0.68,
  c2: "#132C44",
  cy: 1.07,
  rx: 0.7782,
  ry: 1.0164,
  glow: "#56718A",
  glowOpacity: 0.02,
  band: "#94C2FF",
  bandOpacity: 1,
  foot: 0.4,
  noise: true,
  noiseSize: 0.1,
  noiseDensity: 1,
  noiseColor: "#78AAD9",
  noiseOpacity: 0.17,
}

const svgUrl = (svg: string) =>
  `url("data:image/svg+xml,${encodeURIComponent(svg)}")`

export function figmaBackgroundUrl(b: FigmaBackground) {
  const W = 1440
  const H = 810
  const r = b.rx * W
  const cy = b.cy * H
  return svgUrl(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid slice">
<defs>
<radialGradient id="g" gradientUnits="userSpaceOnUse" cx="720" cy="${cy}" r="${r}" gradientTransform="translate(720 ${cy}) scale(1 ${(b.ry * H) / r}) translate(-720 ${-cy})">
<stop offset="0" stop-color="${b.c0}"/><stop offset="${b.c1At}" stop-color="${b.c1}"/><stop offset="1" stop-color="${b.c2}"/>
</radialGradient>
<filter id="b" filterUnits="userSpaceOnUse" x="-1400" y="-400" width="4240" height="1800"><feGaussianBlur stdDeviation="116.35"/></filter>
</defs>
<rect width="${W}" height="${H}" fill="url(#g)"/>
<ellipse cx="720" cy="705.26" rx="569.39" ry="231.7" fill="${b.glow}" fill-opacity="${b.glowOpacity}" filter="url(#b)"/>
<ellipse cx="720" cy="810" rx="1309.41" ry="77.74" fill="${b.band}" fill-opacity="${b.bandOpacity}" filter="url(#b)"/>
</svg>`)
}

/**
 * A 256px grain tile. Figma's noise size 0.2 is grain of about a pixel, so
 * the turbulence frequency is 0.17 / size. Density drops the faintest grains:
 * at 100% every pixel carries some, lower keeps only the stronger ones. The
 * colour's alpha is applied as the layer's opacity.
 */
export function figmaNoiseUrl(b: FigmaBackground) {
  if (!b.noise) return "none"
  const freq = Math.min(2, 0.17 / Math.max(0.02, b.noiseSize))
  const hex = b.noiseColor.replace("#", "")
  const [r, g, bl] = [0, 2, 4].map(
    (i) => parseInt(hex.slice(i, i + 2), 16) / 255
  )
  // alpha = k · noise − cut: density 1 → cut 0, density 0.2 → most grains gone.
  const k = 2.2
  const cut = (1 - b.noiseDensity) * 1.4
  return svgUrl(`<svg xmlns="http://www.w3.org/2000/svg" width="256" height="256">
<filter id="n" x="0" y="0" width="100%" height="100%"><feTurbulence type="fractalNoise" baseFrequency="${freq}" numOctaves="3" stitchTiles="stitch"/>
<feColorMatrix type="matrix" values="0 0 0 0 ${r} 0 0 0 0 ${g} 0 0 0 0 ${bl} ${k} 0 0 0 ${-0.3 * k - cut}"/></filter>
<rect width="256" height="256" filter="url(#n)"/>
</svg>`)
}

export function figmaBackgroundVars(b: FigmaBackground): CSSProperties {
  return {
    "--study-figma": figmaBackgroundUrl(b),
    "--study-noise": figmaNoiseUrl(b),
    "--study-noise-opacity": b.noiseOpacity,
    "--study-foot": b.foot,
  } as CSSProperties
}

// Earlier approved frame: coast 3, 24px blur, centered at 100%, no overlay.
// Deliberately independent of old preview query parameters and local storage.
export function HomeV4BaselineBackground({
  children,
}: {
  children: ReactNode
}) {
  return (
    <div
      data-background-study="figma"
      style={
        {
          ...figmaBackgroundVars(FIGMA_BACKGROUND_DEFAULTS),
          "--study-blur": "24px",
          "--study-scale": 1.15,
          "--study-x": "50%",
          "--study-y": "50%",
          "--study-image":
            "url('https://wvms6gve4aqx5vj4.public.blob.vercel-storage.com/images/experiments/baseline-backgrounds/midjourney-coast-dark-3.png')",
        } as CSSProperties
      }
    >
      {children}
      {/* ds-color-ignore — approved baseline background palette. */}
      <style>{`
        #baseline-background-study{scroll-margin-top:100px}
        .background-study-card{overflow:clip;clip-path:inset(0 round 32px);background-color:#102c36}
        .background-study-photo{display:none;position:absolute;inset:calc(-3 * var(--study-blur,32px));z-index:-1;pointer-events:none;background-size:cover;background-position:center}
        [data-background-study]:not([data-background-study=original]) .background-study-original{visibility:hidden}
        [data-background-study]:not([data-background-study=original]) .background-study-photo{display:block}
        [data-background-study][data-background-study=coastphoto] .background-study-photo,[data-background-study][data-background-study=coastrich] .background-study-photo{background-image:var(--study-image);filter:blur(var(--study-blur));transform:scale(var(--study-scale));transform-origin:var(--study-x) var(--study-y)}
        [data-background-study=figma] .background-study-photo{inset:0;background-image:var(--study-figma);background-size:100% 100%}
        [data-background-study=figma] .background-study-photo::after{content:"";position:absolute;inset:0;background-image:var(--study-noise);background-size:256px 256px;opacity:var(--study-noise-opacity);pointer-events:none}
        @media (max-width:1023px){[data-background-study=figma] .background-study-photo{background-image:linear-gradient(to bottom,transparent 30%,rgb(43 62 79 / var(--study-foot)) 100%),var(--study-figma)}}
        [data-background-study]:not([data-background-study=original]) .background-study-card .hv3-bl-strip{background:rgba(255,255,255,.09);-webkit-backdrop-filter:none;backdrop-filter:none;box-shadow:inset 0 0 0 1px rgba(255,255,255,.22)}
        [data-background-study]:not([data-background-study=original]) .background-study-card .hv3-bl-strip:hover{background:rgba(255,255,255,.13);box-shadow:inset 0 0 0 1px rgba(255,255,255,.34)}
        [data-background-study]:not([data-background-study=original]) .background-study-card .hv3-bl-strip[data-on]{background:rgba(255,255,255,.13);box-shadow:inset 0 0 0 1.5px #65b957}
      `}</style>
    </div>
  )
}
